<template>
  <div class="app-wrapper" :class="{ 'is-dark-layout': isDarkLayout }">
    <aside class="sidebar">
      <div class="logo">
        <img src="/logo.png" alt="邯郸市研学旅行协会" />
      </div>
      <el-scrollbar class="l1-scroll">
        <div
          v-for="item in menus"
          :key="'l1-' + item.id"
          class="l1-item"
          :class="{ active: activeL1 && activeL1.id === item.id }"
          @click="selectL1(item)"
        >
          <i :class="item.icon || 'el-icon-menu'" />
          <span>{{ item.title }}</span>
        </div>
      </el-scrollbar>
    </aside>

    <aside class="sub-sidebar" v-show="showL2 && !subSidebarCollapsed">
      <div class="sub-title">{{ activeL1 ? activeL1.title : '' }}</div>
      <el-scrollbar class="l2-scroll">
        <div
          v-for="child in l2Menus"
          :key="'l2-' + child.id"
          class="l2-item"
          :class="{ active: activeMenu === resolvePath(activeL1.path, child.path) }"
          @click="goPage(resolvePath(activeL1.path, child.path))"
        >
          {{ child.title }}
        </div>
      </el-scrollbar>
    </aside>

    <div class="main">
      <div class="navbar">
        <div class="navbar-left">
          <button type="button" class="nav-icon-btn" :title="subSidebarCollapsed ? '展开菜单' : '收起菜单'" @click="toggleSubSidebar">
            <i :class="subSidebarCollapsed ? 'el-icon-s-unfold' : 'el-icon-s-fold'" />
          </button>
          <el-breadcrumb separator="/" class="nav-breadcrumb">
            <el-breadcrumb-item v-for="(item, index) in breadcrumbs" :key="index">
              <router-link v-if="item.path && index < breadcrumbs.length - 1" :to="item.path">{{ item.title }}</router-link>
              <span v-else>{{ item.title }}</span>
            </el-breadcrumb-item>
          </el-breadcrumb>
        </div>
        <div class="navbar-right">
          <el-tooltip content="待办提醒" placement="bottom">
            <button type="button" class="nav-icon-btn badge-wrap" @click="goDashboard">
              <i class="el-icon-bell" />
              <span v-if="pendingTotal > 0" class="nav-badge">{{ pendingTotal > 99 ? '99+' : pendingTotal }}</span>
            </button>
          </el-tooltip>
          <el-tooltip :content="isFullscreen ? '退出全屏' : '全屏'" placement="bottom">
            <button type="button" class="nav-icon-btn" @click="toggleFullscreen">
              <i :class="isFullscreen ? 'el-icon-copy-document' : 'el-icon-full-screen'" />
            </button>
          </el-tooltip>
          <el-tooltip content="刷新当前页" placement="bottom">
            <button type="button" class="nav-icon-btn" @click="refreshPage">
              <i class="el-icon-refresh" />
            </button>
          </el-tooltip>
          <el-dropdown trigger="click" @command="onUserCommand">
            <div class="user-trigger">
              <span class="user-avatar">{{ avatarText }}</span>
              <span class="user-name">{{ name }}</span>
              <i class="el-icon-arrow-down caret" />
            </div>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="logout">退出登录</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </div>
      <tags-view v-if="!isDarkLayout" ref="tags" />
      <div class="app-main" :class="{ 'is-dark': isDarkLayout }">
        <router-view v-if="alive" :key="routeKey" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import TagsView from './components/TagsView.vue'
import request from '@/utils/request'

export default {
  name: 'Layout',
  components: { TagsView },
  data() {
    return {
      alive: true,
      activeL1Id: null,
      subSidebarCollapsed: false,
      isFullscreen: false,
      pendingStats: {}
    }
  },
  computed: {
    ...mapGetters(['menus', 'name']),
    activeMenu() {
      const from = this.$route.query.from
      if (from && typeof from === 'string') return from
      const meta = this.$route.meta || {}
      if (Array.isArray(meta.menuPaths) && meta.menuPaths.length) {
        return meta.menuPaths[0]
      }
      if (meta.menuPath) {
        return meta.menuPath
      }
      return this.$route.path
    },
    routeKey() {
      return this.$route.fullPath
    },
    activeL1() {
      const list = this.menus || []
      if (!list.length) return null
      const byId = list.find(m => m.id === this.activeL1Id)
      if (byId) return byId
      return this.findL1ByRoute(this.$route.path) || list[0]
    },
    l2Menus() {
      const l1 = this.activeL1
      if (!l1) return []
      if (l1.children && l1.children.length) return l1.children
      return [{ id: 'self-' + l1.id, title: l1.title, path: l1.path, _leaf: true }]
    },
    showL2() {
      return !!(this.activeL1)
    },
    isDarkLayout() {
      return !!(this.$route.meta && this.$route.meta.darkLayout)
    },
    activeL2() {
      const path = this.activeMenu
      const list = this.l2Menus || []
      let matched = null
      list.forEach(item => {
        const p = this.resolvePath(this.activeL1.path, item.path)
        if (path === p || path.startsWith(p + '/')) {
          if (!matched || p.length > this.resolvePath(this.activeL1.path, matched.path).length) {
            matched = item
          }
        }
      })
      return matched
    },
    breadcrumbs() {
      const items = []
      const l1 = this.activeL1
      if (l1) {
        items.push({ title: l1.title, path: this.l2Menus.length ? this.normalizePath(l1.path) : '' })
      }
      const l2 = this.activeL2
      if (l2 && !l2._leaf) {
        items.push({ title: l2.title, path: this.resolvePath(l1.path, l2.path) })
      }
      const pageTitle = (this.$route.meta && this.$route.meta.title) || ''
      if (pageTitle && (!items.length || items[items.length - 1].title !== pageTitle)) {
        items.push({ title: pageTitle })
      }
      return items.length ? items : [{ title: '首页', path: '/dashboard' }]
    },
    avatarText() {
      const n = String(this.name || '管').trim()
      return n.slice(0, 1)
    },
    pendingTotal() {
      const keys = ['pending_org', 'pending_course', 'pending_activity', 'complaint_pending']
      return keys.reduce((sum, key) => sum + Number(this.pendingStats[key] || 0), 0)
    }
  },
  created() {
    this.loadPendingStats()
    document.addEventListener('fullscreenchange', this.syncFullscreen)
  },
  beforeDestroy() {
    document.removeEventListener('fullscreenchange', this.syncFullscreen)
  },
  watch: {
    '$route.path': {
      immediate: true,
      handler(path) {
        const l1 = this.findL1ByRoute(path)
        if (l1) this.activeL1Id = l1.id
      }
    },
    menus: {
      immediate: true,
      handler(list) {
        if ((!this.activeL1Id || !list.find(m => m.id === this.activeL1Id)) && list && list.length) {
          const l1 = this.findL1ByRoute(this.$route.path) || list[0]
          this.activeL1Id = l1.id
        }
      }
    },
    '$route.path'() {
      this.loadPendingStats()
    }
  },
  methods: {
    async loadPendingStats() {
      if (!this.$store.getters.token) return
      try {
        const res = await request({ url: '/adminapi/ops/dashboard', method: 'get' })
        this.pendingStats = res.data || {}
      } catch (e) {
        this.pendingStats = {}
      }
    },
    toggleSubSidebar() {
      this.subSidebarCollapsed = !this.subSidebarCollapsed
    },
    goDashboard() {
      if (this.$route.path !== '/dashboard') {
        this.$router.push('/dashboard').catch(() => {})
      }
    },
    syncFullscreen() {
      this.isFullscreen = !!document.fullscreenElement
    },
    toggleFullscreen() {
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {})
      } else {
        document.documentElement.requestFullscreen().catch(() => {})
      }
    },
    onUserCommand(cmd) {
      if (cmd === 'logout') this.logout()
    },
    findL1ByRoute(path) {
      const p = this.normalizePath(path)
      const list = this.menus || []
      for (const item of list) {
        if (item.children && item.children.length) {
          for (const child of item.children) {
            if (this.resolvePath(item.path, child.path) === p) return item
          }
          const parent = this.normalizePath(item.path)
          if (p === parent || p.startsWith(parent + '/')) return item
        } else if (this.normalizePath(item.path) === p) {
          return item
        }
      }
      return null
    },
    selectL1(item) {
      this.activeL1Id = item.id
      if (item.children && item.children.length) {
        const first = item.children[0]
        this.goPage(this.resolvePath(item.path, first.path))
      } else {
        this.goPage(this.normalizePath(item.path))
      }
    },
    goPage(path) {
      const p = this.normalizePath(path)
      if (this.$route.path !== p) {
        this.$router.push(p).catch(() => {})
      }
    },
    resolvePath(parent, child) {
      if (child && typeof child === 'object') {
        if (child._leaf) return this.normalizePath(parent)
        return this.resolvePath(parent, child.path)
      }
      if (!child) return this.normalizePath(parent)
      if (String(child).startsWith('/')) return this.normalizePath(child)
      const p = String(parent || '').replace(/\/$/, '')
      return this.normalizePath(p + '/' + child)
    },
    normalizePath(path) {
      if (!path) return '/'
      let p = String(path).replace(/\/+/g, '/')
      if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1)
      return p
    },
    refreshPage() {
      this.alive = false
      this.$nextTick(() => {
        this.alive = true
        this.$message.success('页面已刷新')
      })
    },
    refresh() {
      this.refreshPage()
    },
    async logout() {
      try {
        await this.$confirm('确认退出登录？', '提示', {
          type: 'warning',
          confirmButtonText: '退出',
          cancelButtonText: '取消'
        })
      } catch (e) {
        return
      }
      await this.$store.dispatch('user/logout')
      this.$router.replace('/login').catch(() => {})
    }
  }
}
</script>

<style lang="scss" scoped>
.app-wrapper {
  display: flex;
  height: 100vh;
  width: 100%;
  overflow: hidden;
  background: #f0f3f8;
}
.sidebar {
  width: 72px;
  height: 100%;
  background: #0b2458;
  color: #fff;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  .logo {
    height: 56px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    img {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: #fff;
      object-fit: contain;
    }
  }
  .l1-scroll {
    flex: 1;
    min-height: 0;
    overflow: hidden;
    ::v-deep .el-scrollbar__wrap {
      overflow-x: hidden !important;
    }
    ::v-deep .el-scrollbar__bar.is-horizontal {
      display: none !important;
    }
  }
}
.l1-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  box-sizing: border-box;
  padding: 12px 4px;
  cursor: pointer;
  color: #a9bbe6;
  transition: all 0.2s ease;
  border-left: 3px solid transparent;
  overflow: hidden;
  i { font-size: 18px; }
  span {
    font-size: 11px;
    line-height: 1.15;
    text-align: center;
    max-width: 100%;
    word-break: break-all;
  }
  &:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.06);
  }
  &.active {
    color: #fff;
    background: rgba(47, 107, 255, 0.28);
    border-left-color: #4d84ff;
  }
}
.sub-sidebar {
  width: 168px;
  height: 100%;
  background: #fff;
  border-right: 1px solid #e6ecf5;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  .sub-title {
    height: 50px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    padding: 0 16px;
    font-size: 15px;
    font-weight: 700;
    color: #1f3b7a;
    border-bottom: 1px solid #eef2f8;
  }
  .l2-scroll {
    flex: 1;
    min-height: 0;
    overflow: hidden;
    ::v-deep .el-scrollbar__wrap {
      overflow-x: hidden !important;
    }
    ::v-deep .el-scrollbar__bar.is-horizontal {
      display: none !important;
    }
  }
}
.l2-item {
  margin: 4px 10px;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 13px;
  color: #4a5d7a;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    background: #f3f6fc;
    color: #2f6bff;
  }
  &.active {
    background: #eaf0ff;
    color: #2f6bff;
    font-weight: 600;
  }
}
.main {
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.navbar {
  height: 50px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  box-shadow: 0 1px 4px rgba(15, 40, 80, 0.06);
  flex-shrink: 0;
  border-bottom: 1px solid #e8edf5;
}
.navbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
}
.navbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.nav-icon-btn {
  position: relative;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 1px solid #e6ecf5;
  border-radius: 6px;
  background: #f7f9fc;
  color: #5a6b85;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  i { font-size: 16px; }
  &:hover {
    color: #2f6bff;
    border-color: #c8d8ff;
    background: #eef3ff;
  }
}
.badge-wrap .nav-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  background: #ff4d4f;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
}
.nav-breadcrumb {
  min-width: 0;
  ::v-deep .el-breadcrumb__inner,
  ::v-deep .el-breadcrumb__inner a {
    color: #667085;
    font-weight: 500;
  }
  ::v-deep .el-breadcrumb__item:last-child .el-breadcrumb__inner,
  ::v-deep .el-breadcrumb__item:last-child .el-breadcrumb__inner span {
    color: #1f3b7a;
    font-weight: 600;
  }
}
.user-trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: 4px;
  padding: 4px 8px 4px 4px;
  border-radius: 20px;
  cursor: pointer;
  transition: background 0.2s ease;
  &:hover { background: #f3f6fc; }
}
.user-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3d6fd9 0%, #2f6bff 100%);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.user-name {
  color: #334155;
  font-size: 13px;
  font-weight: 500;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.caret {
  font-size: 12px;
  color: #98a2b3;
}
.app-main {
  flex: 1;
  overflow: auto;
  padding: 12px 16px 24px;
  min-height: 0;

  &.is-dark {
    padding: 0;
    overflow: hidden;
    background: #060d18;
  }
}
.app-wrapper.is-dark-layout {
  background: #060d18;

  .navbar {
    background: #0a1525;
    border-bottom-color: rgba(0, 180, 255, 0.12);
    box-shadow: none;
  }
  .nav-icon-btn {
    background: rgba(0, 180, 255, 0.08);
    border-color: rgba(0, 180, 255, 0.2);
    color: #8eb4d4;
    &:hover {
      color: #4fc3f7;
      border-color: rgba(0, 180, 255, 0.4);
      background: rgba(0, 180, 255, 0.15);
    }
  }
  .nav-breadcrumb {
    ::v-deep .el-breadcrumb__inner,
    ::v-deep .el-breadcrumb__inner a {
      color: #6a8fad;
    }
    ::v-deep .el-breadcrumb__item:last-child .el-breadcrumb__inner,
    ::v-deep .el-breadcrumb__item:last-child .el-breadcrumb__inner span {
      color: #e8f4ff;
    }
    ::v-deep .el-breadcrumb__separator {
      color: #4a6580;
    }
  }
  .user-trigger:hover { background: rgba(0, 180, 255, 0.08); }
  .user-name { color: #d4e8f8; }
  .caret { color: #6a8fad; }
  .sub-sidebar {
    background: #0a1525;
    border-right-color: rgba(0, 180, 255, 0.12);
  }
  .sub-title {
    color: #e8f4ff;
    border-bottom-color: rgba(0, 180, 255, 0.12);
  }
  .l2-item {
    color: #8eb4d4;
    &:hover {
      background: rgba(0, 180, 255, 0.08);
      color: #4fc3f7;
    }
    &.active {
      background: rgba(0, 180, 255, 0.15);
      color: #4fc3f7;
    }
  }
}
</style>
